import React, { RefObject, useEffect } from 'react';
import { ReactElement, useCallback, useMemo } from 'react';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { DrawApi } from '@apply/api';
import { DrawBudgetLineCategory, DrawBudgetLineCategoryDescription, DrawType } from '@apply/constants';
import { Column, isMobileScreen, MobileScrollData } from '../../../';
import { formatCurrencyOrUndefined } from '../../../../../ui/src';
import {
    Editing,
    Grouping,
    Sorting,
    DataGrid,
    RemoteOperations,
    Paging,
    KeyboardNavigation,
    Summary,
    TotalItem,
} from 'devextreme-react/data-grid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt, faFileInvoiceDollar } from '@fortawesome/free-solid-svg-icons';
import { ConfirmModal } from '@apply/ui';
import useAwaitableComponent from 'use-awaitable-component';
import { ApiResponse } from '@apply/api/src/api/bfiApi';
import { IDrawBudgetItem, IDrawItem, IPostSaveDrawItem } from '@apply/api/src/api/DrawApi';
import { CustomStore } from '@apply/ui';
import dxDataGrid from 'devextreme/ui/data_grid';
import {
    BLUE_BACKGROUND_DARK,
    BLUE_BACKGROUND_LIGHT,
    GRAY_BACKGROUND_DARK,
    GRAY_BACKGROUND_LIGHT,
    PURPLE_BACKGROUND_DARK,
    PURPLE_BACKGROUND_LIGHT,
    YELLOW_BACKGROUND_DARK,
    YELLOW_BACKGROUND_LIGHT,
    DARK_GREY_LETTERING,
    GREEN_LETTERING,
    RED_LETTERING,
    EMPASIZED_LETTERING,
} from './constants.js';
import { DrawBudgetCategory, getDrawBudgetCategories } from './functions/index.js';

interface IDrawGridParameters {
    dataGridRef?: RefObject<DataGrid>;
    applicationInsights: ApplicationInsights;
    drawBudgetId: number;
    draws?: DrawApi.IDraw[];
    noDraws: boolean;
    equityRequirement: number;
    drawColumns: ReactElement<any, string | React.JSXElementConstructor<any>>[][][] | undefined;
    drawTotalItems: ReactElement<any, string | React.JSXElementConstructor<any>>[][][] | undefined;
    currentApproval?: DrawApi.IDrawBudgetApproval;
    set_categories: React.Dispatch<React.SetStateAction<DrawBudgetCategory[]>>;
    set_currentApproval: (currentApproval: DrawApi.IDrawBudgetApproval | undefined) => void;
    set_isEquityMet: React.Dispatch<React.SetStateAction<boolean>>;
    set_totalFunded: React.Dispatch<React.SetStateAction<number>>;
    set_latestDrawFundedTotal: React.Dispatch<React.SetStateAction<number>>;
    set_latestDrawBorrowerRequestTotal: React.Dispatch<React.SetStateAction<number>>;
    set_latestDrawCreatedDate: React.Dispatch<React.SetStateAction<Date>>;
    set_isSnackbarOpen: (isSnackbarOpen: boolean) => void;
    set_snackbarMessage: (snackbarMessage: string) => void;
    isAdmin?: boolean;
    anyAcquisitionAmount: boolean;
    tableWidth: number;
    mobileScreen: boolean;
    mobileScrollData: MobileScrollData;
}

const _DrawGrid = ({
    dataGridRef,
    applicationInsights,
    drawBudgetId,
    draws,
    noDraws,
    equityRequirement,
    drawColumns,
    drawTotalItems,
    currentApproval,
    set_categories,
    set_currentApproval,
    set_isEquityMet,
    set_totalFunded,
    set_latestDrawFundedTotal,
    set_latestDrawBorrowerRequestTotal,
    set_latestDrawCreatedDate,
    set_isSnackbarOpen,
    set_snackbarMessage,
    isAdmin = false,
    anyAcquisitionAmount,
    tableWidth,
    mobileScreen,
    mobileScrollData,
}: IDrawGridParameters): JSX.Element => {
    const columnBorder = mobileScreen ? '1px solid #ddd' : '2px solid #344077';
    const placeHolderRow = useMemo(
        () => ({
            id: -1,
            description: '',
            budgetAmount: 0,
            equityAmount: 0,
            acquisitionAmount: 0,
            hasReceipts: false,
            category: DrawBudgetLineCategory.Other,
        }),
        []
    );

    const [
        statusConfirmEditBudget,
        openConfirmEditBudget,
        resolveConfirmEditBudget,
        rejectConfirmEditBudget,
        resetConfirmEditBudget,
    ] = useAwaitableComponent();
    const showConfirmEditBudgetModal = statusConfirmEditBudget === 'awaiting';

    const [
        statusConfirmEditDescription,
        openConfirmEditDescription,
        resolveConfirmEditDescription,
        rejectConfirmEditDescription,
        resetConfirmEditDescription,
    ] = useAwaitableComponent();
    const showConfirmEditDescriptionModal = statusConfirmEditDescription === 'awaiting';

    const { largeScreen, columnWidth, acquisitionOffset, secondColumnIndex, preparedMobileColumns } = mobileScrollData;

    const drawApi = new DrawApi.DrawApi(applicationInsights);

    const getPropertyStartingWith = (obj: Record<string, any>, searchString: string): string | undefined => {
        return Object.keys(obj).find((key) => key.startsWith(searchString));
    };

    const customStore = useMemo(
        () =>
            new CustomStore({
                key: 'id',
                load: async () => {
                    if (!drawBudgetId) return { data: [] };

                    const response: ApiResponse<IDrawBudgetItem[]> = await drawApi.getDrawTable(drawBudgetId);

                    if (response.statusCode !== 200) throw Error('Could not load table data');

                    const budgetItems: IDrawBudgetItem[] = response.result;
                    if (isAdmin && (draws?.length ?? 0) === 0) {
                        const activeCategories: DrawBudgetLineCategory[] = [];
                        budgetItems.forEach((item) => {
                            if (!activeCategories.includes(item.category)) activeCategories.push(item.category);
                        });

                        for (const [, value] of Object.entries(DrawBudgetLineCategory)) {
                            if (isNaN(Number(value)) && !activeCategories.includes(DrawBudgetLineCategory[value])) {
                                activeCategories.push(DrawBudgetLineCategory[value]);

                                budgetItems.push({
                                    ...placeHolderRow,
                                    category: DrawBudgetLineCategory[value],
                                });
                            }
                        }
                    }

                    set_categories(getDrawBudgetCategories(budgetItems));

                    return budgetItems;
                },
                update: async (key, values) => {
                    if ('description' in values) {
                        if (currentApproval) {
                            try {
                                await openConfirmEditDescription();

                                // Submit Click
                                drawApi.clearApproval(drawBudgetId);
                                set_currentApproval(undefined);
                                return drawApi
                                    .drawBudgetItemDescription(key, values.description)
                                    .then((response) => response.result);
                            } catch (err) {
                                return;
                            } finally {
                                resetConfirmEditDescription();
                            }
                        } else {
                            return drawApi
                                .drawBudgetItemDescription(key, values.description)
                                .then((response) => response.result)
                                .then(() => dataGridRef?.current?.instance.refresh(true));
                        }
                    }
                    if ('budgetAmount' in values) {
                        if (currentApproval) {
                            try {
                                await openConfirmEditBudget();

                                // Submit Click
                                drawApi.clearApproval(drawBudgetId);
                                set_currentApproval(undefined);
                                return drawApi
                                    .drawBudgetItemBudgetAmount(key, values.budgetAmount)
                                    .then((response) => response.result);
                            } catch (err) {
                                return;
                            } finally {
                                resetConfirmEditBudget();
                            }
                        } else {
                            return drawApi
                                .drawBudgetItemBudgetAmount(key, values.budgetAmount)
                                .then((response) => response.result)
                                .then(() => dataGridRef?.current?.instance.refresh(true));
                        }
                    }
                    if ('equityAmount' in values) {
                        return drawApi
                            .drawBudgetItemEquityAmount(key, values.equityAmount)
                            .then((response) => response.result)
                            .then(() => dataGridRef?.current?.instance.refresh(true));
                    }
                    if ('acquisitionAmount' in values) {
                        return drawApi
                            .drawBudgetItemAcquisitionAmount(key, values.acquisitionAmount)
                            .then((response) => response.result)
                            .then(() => dataGridRef?.current?.instance.refresh(true));
                    }

                    const dataFieldString = getPropertyStartingWith(values, 'drawItems');
                    if (dataFieldString) {
                        const [_, propertyToUpdate, drawId, drawNumber] = dataFieldString.split('-');

                        const data: IPostSaveDrawItem = {
                            drawBudgetItemId: key,
                            drawId,
                            drawNumber,
                            value: values[dataFieldString],
                        };

                        if (propertyToUpdate === 'borrowerRequest') {
                            const budgetItems = getDrawBudgetItemsFromGrid();
                            const item = budgetItems.find((item) => item.id === data.drawBudgetItemId);
                            if (!item) throw Error('Could not find budget line item to update');

                            const fundedLineTotal = item.drawItems?.reduce((sum, value) => (sum += value?.funded ?? 0), 0) ?? 0;
                            let availableToBeDrawn = item.budgetAmount - item.equityAmount - fundedLineTotal;
                            availableToBeDrawn = Math.round(availableToBeDrawn * 100) / 100; // Round to two decimal places, BFIA-4395

                            if (data.value > availableToBeDrawn) {
                                set_snackbarMessage(
                                    `Cannot exceed ${formatCurrencyOrUndefined(availableToBeDrawn, 2)} remaining in ${item.description}`
                                );
                                set_isSnackbarOpen(true);
                                return;
                            }

                            return drawApi
                                .drawItemBorrowerRequest(data)
                                .then((response) => response.result)
                                .then(() => dataGridRef?.current?.instance.refresh(true));
                        }
                        if (propertyToUpdate === 'inspection') {
                            return drawApi
                                .drawItemInspection(data)
                                .then((response) => response.result)
                                .then(() => dataGridRef?.current?.instance.refresh(true));
                        }
                        if (propertyToUpdate === 'funded') {
                            return drawApi
                                .drawItemFunded(data)
                                .then((response) => response.result)
                                .then(() => dataGridRef?.current?.instance.refresh(true));
                        }
                        if (propertyToUpdate === 'notes') {
                            return drawApi
                                .drawItemNotes(data)
                                .then((response) => response.result)
                                .then(() => dataGridRef?.current?.instance.refresh(true));
                        }
                    }

                    throw new Error('Could not determine type to update');
                },
                insert: (values: never) => {
                    const category = values as DrawBudgetLineCategory;
                    return drawApi.addDrawBudgetItem(drawBudgetId, category).then((response) => response.result);
                },
                onInserted: () => {
                    dataGridRef?.current?.instance.refresh();
                },
                remove: (key) => {
                    return drawApi.deleteDrawBudgetItem(key);
                },
            }),

        [drawBudgetId, currentApproval]
    );

    useEffect(() => {
        let latestDrawBorrowerRequestTotal = 0;
        let latestDrawFundedTotal = 0;
        if (draws && draws?.length > 0) {
            const latestDraw = draws[draws?.length - 1];
            if (latestDraw) {
                latestDrawFundedTotal = latestDraw.drawItems?.reduce((a, b) => (a = a + b.funded), 0) as number;
                latestDrawBorrowerRequestTotal = latestDraw.drawItems?.reduce((a, b) => (a = a + b.borrowerRequest), 0) as number;
                set_latestDrawCreatedDate(latestDraw.borrowerRequested);
            }
        }
        set_latestDrawBorrowerRequestTotal(latestDrawBorrowerRequestTotal);
        set_latestDrawFundedTotal(latestDrawFundedTotal);
    }, [draws]);

    const onCellPrepared = useCallback(
        (e) => {
            if (e.rowType !== 'data' && e.rowType !== 'header') return;

            if (e.rowType === 'header' && e.column.name !== 'description') {
                e.cellElement.style.textAlign = 'Center';
            }

            // Set the width of the 'DRAW #' columns to 90px for mobile
            const mobileScreen = isMobileScreen();
            if (e.column.caption?.startsWith('DRAW #') && mobileScreen) {
                e.column.width = '90px';
            }

            // Add background color to columns
            const coloredColumns = ['inspection', 'funded', 'borrowerRequest'];
            const tokens = e.column.dataField?.split('-');

            if (e.column.name === 'available' && e.rowType === 'data') availableColumnText(e);
            if (['budgetAmount', 'available'].includes(e.column.name)) mainColumnColor(e);

            if (!tokens || tokens?.length < 2) return;

            if (!coloredColumns.includes(tokens[1])) return;

            const [_, drawItemPropertyName, ___, drawNumber] = tokens;
            colorDrawColumns(e, drawItemPropertyName, drawNumber, isAdmin, draws);
        },
        [drawBudgetId]
    );

    const availableColumnText = (e) => {
        if (e.displayValue > 0) {
            e.cellElement.style.color = GREEN_LETTERING;
        } else if (e.displayValue < 0) {
            e.cellElement.style.color = RED_LETTERING;
        } else {
            e.cellElement.style.color = DARK_GREY_LETTERING;
        }
    };

    const mainColumnColor = (e) => {
        e.cellElement.style.backgroundColor = e.isAltRow ? BLUE_BACKGROUND_DARK : BLUE_BACKGROUND_LIGHT;
        e.cellElement.style.borderLeft = columnBorder;
        e.cellElement.style.borderRight = columnBorder;

        if (e.rowType === 'header') {
            e.cellElement.style.color = EMPASIZED_LETTERING;
            e.cellElement.style.borderTop = columnBorder;
        }
    };

    const colorDrawColumns = (e, drawItemPropertyName, drawNumber, _isAdmin, _draws) => {
        if (!_draws) return;
        if (!drawNumber) return;

        if (e.rowType === 'header') {
            e.cellElement.style.color = EMPASIZED_LETTERING;
        }

        if (_draws[drawNumber]?.isFinalized) {
            e.cellElement.style.backgroundColor = e.isAltRow ? PURPLE_BACKGROUND_DARK : PURPLE_BACKGROUND_LIGHT;
        } else {
            if (
                _draws[drawNumber]?.needsBorrowerSignature == true ||
                (_draws[drawNumber]?.borrowerSubmitted == false && _draws[drawNumber]?.drawType === DrawType.Manual)
            ) {
                e.cellElement.style.backgroundColor = e.isAltRow ? YELLOW_BACKGROUND_DARK : YELLOW_BACKGROUND_LIGHT;
            } else {
                if ((_draws[drawNumber[0]]?.drawType as DrawType) === DrawType.Manual) {
                    // Manual Draw
                    if (drawItemPropertyName === 'borrowerRequest' && _draws[drawNumber]?.borrowerSubmitted && !_isAdmin) {
                        e.cellElement.style.backgroundColor = e.isAltRow ? GRAY_BACKGROUND_DARK : GRAY_BACKGROUND_LIGHT;
                    } else if (drawItemPropertyName === 'funded' && _draws[drawNumber]?.hasResubmitted && !_isAdmin) {
                        e.cellElement.style.backgroundColor = e.isAltRow ? GRAY_BACKGROUND_DARK : GRAY_BACKGROUND_LIGHT;
                    } else {
                        e.cellElement.style.backgroundColor = e.isAltRow ? BLUE_BACKGROUND_DARK : BLUE_BACKGROUND_LIGHT;
                        e.cellElement.style.borderLeft = columnBorder;
                        e.cellElement.style.borderRight = columnBorder;

                        if (e.rowType === 'header') {
                            e.cellElement.style.color = EMPASIZED_LETTERING;
                            e.cellElement.style.borderTop = columnBorder;
                        }
                    }
                } else {
                    // Automatic Draw
                    if (e.column.caption.startsWith('DRAW #') && !_isAdmin) {
                        e.cellElement.style.backgroundColor = e.isAltRow ? GRAY_BACKGROUND_DARK : GRAY_BACKGROUND_LIGHT;
                    } else {
                        e.cellElement.style.backgroundColor = e.isAltRow ? BLUE_BACKGROUND_DARK : BLUE_BACKGROUND_LIGHT;
                        e.cellElement.style.borderLeft = columnBorder;
                        e.cellElement.style.borderRight = columnBorder;

                        if (e.rowType === 'header') {
                            e.cellElement.style.color = EMPASIZED_LETTERING;
                            e.cellElement.style.borderTop = columnBorder;
                        }
                    }
                }
            }
        }
    };

    const onDeleteClick = useCallback(
        (rowIndex) => {
            dataGridRef?.current?.instance.deleteRow(rowIndex);
        },
        [dataGridRef]
    );

    const getDrawBudgetItemsFromGrid = (): IDrawBudgetItem[] => {
        if (!dataGridRef?.current?.instance) return [];

        const dataSource = dataGridRef?.current?.instance.getDataSource();

        if (!dataSource) return [];

        const drawBudgetItems = dataSource.items().reduce((acc, group) => acc.concat(group.items ?? group.collapsedItems), []);

        return drawBudgetItems;
    };

    const onAddBudgetItemClick = useCallback(
        async (category) => {
            customStore.insert(category);
        },
        [dataGridRef, drawApi, drawBudgetId]
    );

    const onDeleteConfirmed = useCallback(
        (e) => {
            const drawBudgetItems = getDrawBudgetItemsFromGrid();

            if (!drawBudgetItems.some((item) => item.category === e.data.category)) {
                const newDrawBudgetItems = [{ ...placeHolderRow, category: e.data.category }];

                drawBudgetItems.forEach((item) => newDrawBudgetItems.push(item));

                dataGridRef?.current?.instance.collapseRow([e.data.category]);
            }
        },
        [dataGridRef, drawApi, placeHolderRow]
    );

    const deleteColumnTemplate = useCallback(
        (cellData: any) => {
            const _receiptIcon = () => (
                <FontAwesomeIcon
                    icon={faFileInvoiceDollar}
                    size={'1x'}
                    className='color-green'
                />
            );

            const _deleteIcon = () => (
                <FontAwesomeIcon
                    icon={faTrashAlt}
                    size={'1x'}
                    onClick={() => onDeleteClick(cellData.rowIndex)}
                    className='color-dull-red'
                />
            );

            if (cellData.data.hasReceipts && isAdmin && noDraws) {
                return (
                    <>
                        <span style={{ position: 'absolute', marginLeft: -24, marginTop: -9 }}>
                            <_receiptIcon />
                        </span>
                        <span style={{ position: 'absolute', marginLeft: -5, marginTop: -9 }}>
                            <_deleteIcon />
                        </span>
                    </>
                );
            }
            if (cellData.data.hasReceipts) {
                return (
                    <>
                        <span style={{ position: 'absolute', marginLeft: -14, marginTop: -9 }}>
                            <_receiptIcon />
                        </span>
                    </>
                );
            }
            if (isAdmin && noDraws) {
                return (
                    <>
                        <span style={{ position: 'absolute', marginLeft: -15, marginTop: -9 }}>
                            <_deleteIcon />
                        </span>
                    </>
                );
            }
            return <></>;
        },
        [isAdmin, noDraws, onDeleteClick]
    );

    const groupTemplate = useCallback(
        (cellData: any) => {
            if (isAdmin && noDraws) {
                return (
                    <div style={{ display: 'flex' }}>
                        <button
                            className='button draw-group-button'
                            onClick={() => onAddBudgetItemClick(cellData.data.key)}
                            title='Add budget item'
                        >
                            Add
                        </button>
                        <div>{DrawBudgetLineCategoryDescription[cellData.value]}</div>
                    </div>
                );
            }

            return DrawBudgetLineCategoryDescription[cellData.value];
        },
        [isAdmin, noDraws, onAddBudgetItemClick]
    );

    const calculateMaximiumAmountFinanced = useCallback(
        (data: DrawApi.IDrawBudgetItem) => data.budgetAmount - (data.equityAmount ?? 0),
        []
    );

    const calculateAmountDrawn = useCallback(
        (data: DrawApi.IDrawBudgetItem) =>
            (data.acquisitionAmount ?? 0) + (data?.drawItems?.reduce((sum, value) => sum + (value?.funded ?? 0), 0) ?? 0),
        []
    );

    const calculateAvailable = useCallback(
        (data: DrawApi.IDrawBudgetItem) => calculateMaximiumAmountFinanced(data) - calculateAmountDrawn(data),
        [calculateAmountDrawn, calculateMaximiumAmountFinanced]
    );

    const calculatePercentageFunded = useCallback(
        (data: DrawApi.IDrawBudgetItem) => {
            const maximiumAmountFinanced = calculateMaximiumAmountFinanced(data);
            const amountDrawn = calculateAmountDrawn(data);

            if (maximiumAmountFinanced <= 0) return undefined;

            return amountDrawn / maximiumAmountFinanced;
        },
        [calculateAmountDrawn, calculateMaximiumAmountFinanced]
    );

    const onContentReady = useCallback(() => {
        if (!isAdmin) return;

        const drawBudgetItems = getDrawBudgetItemsFromGrid();

        for (const [key, value] of Object.entries(DrawBudgetLineCategory)) {
            if (isNaN(Number(value))) {
                if (
                    drawBudgetItems.some((item) => !item || (item.id === -1 && item.category === DrawBudgetLineCategory[value]))
                ) {
                    dataGridRef?.current?.instance.collapseRow([Number(key)]);
                } else {
                    dataGridRef?.current?.instance.expandRow([Number(key)]);
                }
            }
        }
    }, [dataGridRef, isAdmin]);

    let landTotal = 0;
    let acquisitionAmountTotal = 0;
    let totalFunded = 0;
    let totalFinanced = 0;
    let totalDrawn = 0;
    const calculateCustomSummary = (options: {
        component: dxDataGrid;
        groupIndex: number;
        name: string;
        summaryProcess: string;
        totalValue: any;
        value: any;
    }) => {
        switch (options.summaryProcess) {
            case 'start':
                options.totalValue = 0;
                landTotal = 0;
                totalFunded = 0;
                totalFinanced = 0;
                totalDrawn = 0;
                break;
            case 'calculate':
                if (options.name && options.name.startsWith('draw')) {
                    // Draw Column
                    const [_, property, __, drawNumber] = options.name.split('-');
                    const item: IDrawItem = options.value?.drawItems.find((i: IDrawItem) => i.drawNumber == parseInt(drawNumber));
                    if (item) {
                        options.totalValue += item[property];
                        if (property === 'funded') {
                            totalFunded += item.funded;
                        }
                    }
                } else {
                    const drawBudgetItem = options.value as IDrawBudgetItem;
                    switch (options.name) {
                        case 'budgetAmount':
                            options.totalValue += drawBudgetItem.budgetAmount;

                            if (drawBudgetItem.category == DrawBudgetLineCategory.LandPurchase) {
                                landTotal += drawBudgetItem.budgetAmount;
                            }
                            break;
                        case 'equityAmount':
                            options.totalValue += drawBudgetItem.equityAmount;
                            break;
                        case 'acquisitionAmount':
                            options.totalValue += drawBudgetItem.acquisitionAmount;
                            acquisitionAmountTotal = options.totalValue;
                            break;
                        case 'maximiumAmountFinanced':
                            options.totalValue += calculateMaximiumAmountFinanced(drawBudgetItem);
                            totalFinanced = options.totalValue;
                            break;
                        case 'amountDrawn':
                            options.totalValue += calculateAmountDrawn(drawBudgetItem);
                            totalDrawn = options.totalValue;
                            break;
                        case 'percentageFunded':
                            options.totalValue = totalDrawn / totalFinanced;
                            break;
                        case 'available':
                            options.totalValue += calculateAvailable(drawBudgetItem);
                            break;
                    }
                }
                break;
            case 'finalize':
                options.totalValue = options.totalValue;
                if (options.name === 'equityAmount') {
                    let isEquityMet = false;
                    if (landTotal === acquisitionAmountTotal) isEquityMet = true;
                    if (options.totalValue >= equityRequirement) isEquityMet = true;

                    set_isEquityMet(isEquityMet);
                } else if (options.name && options.name.startsWith('draw')) {
                    const [_, property, __, drawNumber] = options.name.split('-');
                    // Most recent Draw Column
                    if (draws && drawNumber === (draws?.length - 1).toString()) {
                        if (property === 'borrowerRequest') {
                            set_latestDrawBorrowerRequestTotal(options.totalValue);
                        }
                        if (property === 'funded') {
                            set_latestDrawFundedTotal(options.totalValue);
                        }
                    }
                    if (property === 'funded') {
                        set_totalFunded(totalFunded);
                    }
                }

                break;
        }
    };

    return (
        <section className='bfi-data-grid-container'>
            <DataGrid
                ref={dataGridRef}
                keyExpr='id'
                allowColumnResizing={false}
                allowColumnReordering={false}
                className='draw-data-grid'
                dataSource={customStore}
                loadPanel={{ enabled: false }}
                onCellPrepared={onCellPrepared}
                onRowRemoved={onDeleteConfirmed}
                onContentReady={onContentReady}
                remoteOperations={false}
                wordWrapEnabled={true}
                width={isMobileScreen() ? undefined : tableWidth}
                showBorders={true}
                showRowLines={true}
                rowAlternationEnabled={true}
                repaintChangesOnly={true}
            >
                <KeyboardNavigation
                    editOnKeyPress={true}
                    enterKeyAction='startEdit'
                    enterKeyDirection='column'
                    enabled={true}
                />
                <RemoteOperations groupPaging={true} />
                <Editing
                    allowUpdating={true}
                    mode='cell'
                    refreshMode='repaint'
                />
                <Sorting mode={'none'} />
                <Grouping
                    expandMode='rowClick'
                    allowCollapsing={false}
                />
                <Paging enabled={false} />

                <Column
                    groupIndex={0}
                    calculateGroupValue='category'
                    groupCellRender={groupTemplate}
                />
                <Column
                    caption=''
                    allowEditing={false}
                    cellRender={deleteColumnTemplate}
                    allowResizing={false}
                    width={columnWidth(20)}
                />
                <Column
                    dataField='description'
                    caption='DESCRIPTION'
                    allowEditing={isAdmin}
                    allowResizing={false}
                    width={columnWidth(200)}
                />
                <Column
                    name='budgetAmount'
                    dataField='budgetAmount'
                    caption='BUDGET AMOUNT'
                    dataType='number'
                    editorOptions={{
                        dataType: 'number',
                        format: { type: 'currency', precision: 2 },
                        step: 0,
                    }}
                    allowEditing={isAdmin}
                    cellRender={(cellData) => formatCurrencyOrUndefined(cellData.value, 0)}
                    format={'currency'}
                    allowResizing={false}
                    width={columnWidth(100)}
                    alignment={'right'}
                    visible={largeScreen ? true : secondColumnIndex === 1}
                />
                <Column
                    dataField='equityAmount'
                    caption='EQUITY REQUIREMENT'
                    dataType='number'
                    editorOptions={{
                        dataType: 'number',
                        format: { type: 'currency', precision: 2 },
                        step: 0,
                    }}
                    allowEditing={isAdmin}
                    cellRender={(cellData) => formatCurrencyOrUndefined(cellData.value, 0)}
                    format={'currency'}
                    width={columnWidth(100)}
                    allowResizing={false}
                    alignment={'right'}
                    visible={largeScreen ? true : secondColumnIndex === 2}
                />
                {(isAdmin || anyAcquisitionAmount) && (
                    <Column
                        dataField='acquisitionAmount'
                        caption='ACQUISITION AMOUNT'
                        dataType='number'
                        editorOptions={{
                            dataType: 'number',
                            format: { type: 'currency', precision: 2 },
                            step: 0,
                        }}
                        allowEditing={isAdmin}
                        cellRender={(cellData) => formatCurrencyOrUndefined(cellData.value, 0)}
                        format={'currency'}
                        width={columnWidth(100)}
                        allowResizing={false}
                        alignment={'right'}
                        visible={largeScreen ? true : secondColumnIndex === 3}
                    />
                )}
                <Column
                    name='maximiumAmountFinanced'
                    calculateCellValue={calculateMaximiumAmountFinanced}
                    cellRender={(cellData) => formatCurrencyOrUndefined(cellData.value, 0)}
                    allowEditing={false}
                    caption='MAXIMUM AMOUNT FINANCED'
                    format={'currency'}
                    width={columnWidth(100)}
                    allowResizing={false}
                    alignment={'right'}
                    visible={largeScreen ? true : secondColumnIndex === (anyAcquisitionAmount ? 4 : 3)}
                />
                {isAdmin && [
                    <Column
                        name='amountDrawn'
                        calculateCellValue={calculateAmountDrawn}
                        cellRender={(cellData) => formatCurrencyOrUndefined(cellData.value, 0)}
                        allowEditing={false}
                        caption='FUNDED including current draw'
                        format={'currency'}
                        width={columnWidth(100)}
                        allowResizing={false}
                        alignment={'right'}
                    />,
                    <Column
                        name='percentageFunded'
                        calculateCellValue={calculatePercentageFunded}
                        allowEditing={false}
                        caption='PERCENTAGE FUNDED'
                        format={'percent'}
                        width={columnWidth(100)}
                        allowResizing={false}
                        alignment={'right'}
                    />,
                ]}
                <Column
                    name='available'
                    calculateCellValue={calculateAvailable}
                    cellRender={(cellData) => formatCurrencyOrUndefined(cellData.value, 2)}
                    allowEditing={false}
                    caption='AVAILABLE'
                    format={'currency'}
                    width={columnWidth(100)}
                    allowResizing={false}
                    alignment={'right'}
                    visible={largeScreen ? true : secondColumnIndex === acquisitionOffset}
                />
                {largeScreen ? drawColumns : preparedMobileColumns}
                <Summary calculateCustomSummary={calculateCustomSummary}>
                    <TotalItem
                        name='budgetAmount'
                        showInColumn='budgetAmount'
                        summaryType='custom'
                        valueFormat={{ type: 'currency', precision: 2 }}
                    />
                    <TotalItem
                        name='equityAmount'
                        showInColumn='equityAmount'
                        summaryType='custom'
                        valueFormat={{ type: 'currency', precision: 2 }}
                    />
                    ,
                    <TotalItem
                        name='acquisitionAmount'
                        showInColumn='acquisitionAmount'
                        summaryType='custom'
                        valueFormat={{ type: 'currency', precision: 2 }}
                    />
                    <TotalItem
                        name='maximiumAmountFinanced'
                        showInColumn='maximiumAmountFinanced'
                        summaryType='custom'
                        valueFormat={{ type: 'currency', precision: 2 }}
                    />
                    <TotalItem
                        name='amountDrawn'
                        showInColumn='amountDrawn'
                        summaryType='custom'
                        valueFormat={{ type: 'currency', precision: 2 }}
                    />
                    <TotalItem
                        name='percentageFunded'
                        showInColumn='percentageFunded'
                        summaryType='custom'
                        valueFormat={'percent'}
                    />
                    <TotalItem
                        name='available'
                        showInColumn='available'
                        summaryType='custom'
                        valueFormat={{ type: 'currency', precision: 2 }}
                    />
                    {largeScreen && drawTotalItems}
                </Summary>
            </DataGrid>
            <ConfirmModal
                isOpen={showConfirmEditBudgetModal}
                text={'Editing an approved budget will require it to be approved again.'}
                confirmCallback={resolveConfirmEditBudget}
                cancelCallback={rejectConfirmEditBudget}
            />
            <ConfirmModal
                isOpen={showConfirmEditDescriptionModal}
                text={'Editing an approved budget will require it to be approved again.'}
                confirmCallback={resolveConfirmEditDescription}
                cancelCallback={rejectConfirmEditDescription}
            />
        </section>
    );
};

export const DrawGrid = React.memo(_DrawGrid);
